import clsx from 'clsx';
import type { CSSProperties, Dispatch, SetStateAction } from 'react';

import styles from './RadialProgress.module.scss';

type RadialProgressProps = {
  minValue: number;
  maxValue: number;
  count: number;
  showCount?: boolean;
  style?: CSSProperties;
  className?: string;
  radius?: number;
  setCount?: Dispatch<SetStateAction<number>>;
};

const RADIAL_RADIUS = 36;

const RadialProgress = ({
  minValue,
  maxValue,
  count,
  showCount,
  style,
  className,
  radius = RADIAL_RADIUS,
}: RadialProgressProps) => {
  const circumference = 2 * Math.PI * radius;
  const progressRange = maxValue - minValue;
  const normalizedProgress =
    progressRange > 0 ? Math.min(Math.max((count - minValue) / progressRange, 0), 1) : 0;
  const strokeOffset = circumference * (1 - normalizedProgress);

  return (
    <div
      style={style}
      className={clsx(styles.radialSlider, className)}
      role="progressbar"
      aria-label="Answered Questions"
      aria-valuemin={minValue}
      aria-valuemax={maxValue}
      aria-valuenow={count}
    >
      <svg className={styles.radialSliderSvg} viewBox="0 0 100 100">
        <circle
          className={styles.radialSliderTrack}
          cx="50"
          cy="50"
          r={radius}
          style={{ strokeDasharray: `${circumference} ${circumference}` }}
        />
        <circle
          className={clsx(styles.radialSliderFill, count === maxValue && styles.filled)}
          cx="50"
          cy="50"
          r={radius}
          style={{
            strokeDasharray: `${circumference} ${circumference}`,
            strokeDashoffset: strokeOffset,
          }}
        />
      </svg>
      {showCount && <span className={styles.radialSliderValue}>{count}</span>}
    </div>
  );
};

export default RadialProgress;
